// MealPrepTimeline — renders the queued prep_notifications rows for a
// single scheduled meal as a horizontal strip of timestamp chips.
// Purpose: give the user a visible preview of what the system will
// ping them about, when, so they trust it before the first ping fires.
//
// Shown inline inside MealDetailDrawer in Plan.jsx. Each chip is:
//   • emoji from the prep step (freezer 🧊 for overnight, ⏰ otherwise)
//   • short "when" label derived from deliver_at
//   • body text of the reminder on a second line
//   • a right-side state pill: PENDING / DELIVERED / OFF
//   • tap → toggle dismissed (opt-out of this particular reminder for
//     this particular meal)

import { FONT, COLOR } from "../lib/tokens";
import { color, font, space, radius, shadow } from "../lib/mcmTokens";

function fmtWhen(deliverIso, scheduledIso) {
  const d = new Date(deliverIso);
  const scheduled = new Date(scheduledIso);
  const now = new Date();
  const ms = d.getTime() - now.getTime();
  const isPast = ms < 0;

  // Relative to the meal: "T-30m", "T-12h", "T-1d". Anchored to
  // scheduled_for so the label reads in cooking-time terms rather
  // than calendar-clock terms.
  const leadMs = scheduled.getTime() - d.getTime();
  const leadMin = Math.round(leadMs / 60_000);
  let leadLabel;
  if (leadMin < 60) {
    leadLabel = `T-${leadMin}m`;
  } else if (leadMin < 24 * 60) {
    leadLabel = `T-${Math.round(leadMin / 60)}h`;
  } else {
    leadLabel = `T-${Math.round(leadMin / (24 * 60))}d`;
  }

  // Absolute clock label for the chip's wall-time, formatted short:
  // today → "9:30 PM", tomorrow → "Tue 9:30 PM", further → "Apr 23 9:30 PM".
  const sameDay = d.toDateString() === now.toDateString();
  const dayLabel = sameDay
    ? ""
    : d.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" });
  const timeLabel = d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
  const absLabel = dayLabel ? `${dayLabel} · ${timeLabel}` : timeLabel;

  return { leadLabel, absLabel, isPast };
}

function stateTone(row) {
  if (row.dismissed_at) return { label: "OFF",      fg: color.inkFaint,  bg: "transparent",        border: color.hairline };
  if (row.delivered_at) return { label: "SENT",     fg: color.teal,      bg: color.tealTint,       border: "rgba(47,143,131,0.32)" };
  return                       { label: "QUEUED",   fg: color.warmBrown, bg: color.mustardTint,    border: "rgba(212,166,55,0.42)" };
}

export default function MealPrepTimeline({ meal, rows, onDismiss, onUndismiss }) {
  if (!meal || !rows?.length) return null;

  // Show deliveries in chronological order — earliest future ping
  // first. Past delivered rows hang on the left (dimmed) so users
  // remember what already fired.
  const sorted = [...rows].sort((a, b) => a.deliver_at.localeCompare(b.deliver_at));

  const queued    = sorted.filter(r => !r.delivered_at && !r.dismissed_at).length;
  const delivered = sorted.filter(r => r.delivered_at).length;

  return (
    <div style={{
      marginTop: space.flow,
      padding: `${space.flow}px ${space.flow}px ${space.inline}px`,
      background: color.glassFillHeavy,
      border: `1px solid ${color.hairline}`,
      borderRadius: radius.card,
      boxShadow: shadow.soft,
    }}>
      <div style={{
        display: "flex", alignItems: "baseline", gap: space.tight,
        marginBottom: space.inline,
      }}>
        <span style={{
          fontFamily: font.mono, fontSize: 10,
          color: color.warmBrown, letterSpacing: "0.14em", fontWeight: 600,
        }}>
          PREP REMINDERS
        </span>
        <span style={{
          fontFamily: font.serif, fontStyle: "italic",
          fontSize: 12, color: color.inkFaint, fontWeight: 400,
        }}>
          — pings before the cook
        </span>
        <span style={{
          fontFamily: font.mono, fontSize: 10,
          color: color.inkFaint, marginLeft: "auto",
          letterSpacing: "0.08em",
        }}>
          {queued} queued · {delivered} sent
        </span>
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: space.tight }}>
        {sorted.map(row => {
          const when = fmtWhen(row.deliver_at, meal.scheduled_for);
          const tone = stateTone(row);
          const dim  = !!row.dismissed_at || !!row.delivered_at;
          const toggle = () => {
            if (row.delivered_at) return; // can't un-send
            if (row.dismissed_at) onUndismiss?.(row.id);
            else                  onDismiss?.(row.id);
          };
          return (
            <button
              key={row.id}
              onClick={toggle}
              disabled={!!row.delivered_at}
              style={{
                display: "flex", alignItems: "center", gap: space.inline,
                padding: `${space.tight}px ${space.inline}px`,
                background: color.paper,
                border: `1px solid ${color.hairline}`,
                borderRadius: radius.field,
                cursor: row.delivered_at ? "default" : "pointer",
                textAlign: "left",
                width: "100%",
                opacity: dim ? 0.65 : 1,
              }}
            >
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{
                  display: "flex", alignItems: "baseline", gap: space.tight,
                  marginBottom: 1,
                }}>
                  <span style={{
                    fontFamily: font.mono, fontSize: 10,
                    color: color.warmBrown, letterSpacing: "0.1em", fontWeight: 700,
                  }}>
                    {when.leadLabel}
                  </span>
                  <span style={{
                    fontFamily: font.mono, fontSize: 10,
                    color: color.inkFaint,
                  }}>
                    {when.absLabel}
                  </span>
                </div>
                <div style={{
                  fontFamily: font.sans, fontSize: 13, fontWeight: 600,
                  color: color.ink, lineHeight: 1.3,
                  whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
                }}>
                  {row.title}
                </div>
                {row.body && (
                  <div style={{
                    fontFamily: font.sans, fontSize: 12,
                    color: color.inkMuted, marginTop: 2,
                    overflow: "hidden", textOverflow: "ellipsis",
                    display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical",
                    lineHeight: 1.35,
                  }}>
                    {row.body}
                  </div>
                )}
              </div>
              <span style={{
                flexShrink: 0,
                fontFamily: font.mono, fontSize: 9,
                color: tone.fg, background: tone.bg,
                border: `1px solid ${tone.border}`,
                borderRadius: 4, padding: "2px 6px",
                letterSpacing: "0.1em", fontWeight: 700,
              }}>
                {tone.label}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
